import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, Settings, Book, AlertTriangle, ChevronDown, ChevronUp, Globe, Smartphone, FileText, Mail, HelpCircle } from 'lucide-react';
import InstallAppButton from './components/InstallAppButton';

const BafangDiagnosticApp = () => {
  const { t, i18n } = useTranslation();
  const [activeTab, setActiveTab] = useState('decoder');
  const [errorCode, setErrorCode] = useState('');
  const [searchResult, setSearchResult] = useState(null);
  const [expandedSections, setExpandedSections] = useState({});
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showLanguageMenu, setShowLanguageMenu] = useState(false);

  // Configurazione lingue
  const languages = [
    { code: 'it', name: 'Italiano', flag: '🇮🇹' },
    { code: 'en', name: 'English', flag: '🇬🇧' },
    { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
    { code: 'fr', name: 'Français', flag: '🇫🇷' },
    { code: 'es', name: 'Español', flag: '🇪🇸' },
    { code: 'pt', name: 'Português', flag: '🇵🇹' },
    { code: 'nl', name: 'Nederlands', flag: '🇳🇱' },
    { code: 'pl', name: 'Polski', flag: '🇵🇱' },
    { code: 'zh', name: '中文', flag: '🇨🇳' }
  ];

  const currentLanguage = languages.find(lang => lang.code === i18n.language) || languages[0];

  // Cambia lingua
  const changeLanguage = (languageCode) => {
    i18n.changeLanguage(languageCode);
    setShowLanguageMenu(false);
  };

  // Link reali del sito Bafang - AGGIORNATI
  const bafangLinks = {
    userManual: 'https://bafang-e.com/en/oem-area/service/UserManual',
    dealerManual: 'https://bafang-e.com/en/oem-area/service/DealerManual',
    bafangGo: 'https://bafang-e.com/en/BafangGo/',
    besstPro: 'https://bafang-e.com/en/service/downloads/',
    catalogProducts: 'https://bafang-e.com/Uploaded/Labels/OEM%20WORKBOOK%20[EN].pdf',
    products: 'https://bafang-e.com/en/products/',
    support: 'https://bafang-e.com/en/service/after-sales-support/',
    faq: 'https://bafang-e.com/en/oem-area/service/faqs'
  };

  // Lista dei codici di errore disponibili
  const availableErrorCodes = [
    'E-05', 'E-06', 'E-07', 'E-08', 'E-09', 'E-10', 'E-11', 'E-12', 'E-13', 'E-14', 'E-15', 'E-18',
    'E-21', 'E-27', 'E-30', 'E-35', 'E-42', 'E-45', 'E-46', 'E-48', 'E-49', 'E-50', 'E-59', 'E-71',
    'E-89', 'E-90', 'E-91', 'E-92', 'E-93', 'E-94', 'E-95', 'E-96', 'E-97', 'E-98', 'E-99',
    'W-22', 'W-25', 'W-26', 'W-33', 'W-36', 'W-41', 'N-22'
  ];

  // Funzione per ottenere categoria da codice
  const getCodeCategory = (code) => {
    const categoryMap = {
      'E-05': 'sensors', 'E-06': 'battery', 'E-07': 'battery', 'E-08': 'motor', 'E-09': 'motor',
      'E-10': 'motor', 'E-11': 'motor', 'E-12': 'motor', 'E-13': 'motor', 'E-14': 'motor',
      'E-15': 'motor', 'E-18': 'motor', 'E-21': 'sensors', 'E-27': 'motor', 'E-30': 'communication',
      'E-35': 'motor', 'E-42': 'battery', 'E-45': 'battery', 'E-46': 'battery', 'E-48': 'battery',
      'E-49': 'battery', 'E-50': 'battery', 'E-59': 'battery', 'E-71': 'security', 'E-89': 'iot',
      'E-90': 'abs', 'E-91': 'abs', 'E-92': 'abs', 'E-93': 'abs', 'E-94': 'abs', 'E-95': 'iot',
      'E-96': 'iot', 'E-97': 'security', 'E-98': 'security', 'E-99': 'iot', 'W-22': 'battery',
      'W-25': 'sensors', 'W-26': 'sensors', 'W-33': 'brakes', 'W-36': 'communication', 'W-41': 'battery',
      'N-22': 'battery'
    };
    return categoryMap[code] || 'motor';
  };

  // Funzioni per gestire categorie e colori
  const getCategoryColor = (category) => {
    const colors = {
      battery: 'bg-green-100 hover:bg-green-200 text-green-800',
      motor: 'bg-red-100 hover:bg-red-200 text-red-800',
      sensors: 'bg-blue-100 hover:bg-blue-200 text-blue-800',
      communication: 'bg-purple-100 hover:bg-purple-200 text-purple-800',
      abs: 'bg-orange-100 hover:bg-orange-200 text-orange-800',
      brakes: 'bg-yellow-100 hover:bg-yellow-200 text-yellow-800',
      iot: 'bg-indigo-100 hover:bg-indigo-200 text-indigo-800',
      security: 'bg-pink-100 hover:bg-pink-200 text-pink-800'
    };
    return colors[category] || 'bg-gray-100 hover:bg-gray-200 text-gray-800';
  };

  const handleSearch = () => {
    let inputCode = errorCode.trim().toUpperCase();

    // Se solo numero, cerca formato E-XX, W-XX, N-XX
    if (/^\d+$/.test(inputCode)) {
      const num = inputCode.padStart(2, '0');
      const possibleCodes = [`E-${num}`, `W-${num}`, `N-${num}`];
      
      for (const code of possibleCodes) {
        if (availableErrorCodes.includes(code)) {
          setErrorCode(code);
          setSearchResult({
            code,
            category: getCodeCategory(code)
          });
          return;
        }
      }
    }

    // Se formato già corretto
    if (availableErrorCodes.includes(inputCode)) {
      setSearchResult({
        code: inputCode,
        category: getCodeCategory(inputCode)
      });
      return;
    }

    // Se formato senza trattino (E5, W25, etc)
    const match = inputCode.match(/^([EWN])(\d+)$/);
    if (match) {
      const letter = match[1];
      const num = match[2].padStart(2, '0');
      const formattedCode = `${letter}-${num}`;
      
      if (availableErrorCodes.includes(formattedCode)) {
        setErrorCode(formattedCode);
        setSearchResult({
          code: formattedCode,
          category: getCodeCategory(formattedCode)
        });
        return;
      }
    }

    // Nessun codice trovato
    setSearchResult({
      code: null,
      notFound: true
    });
  };

  const openManual = (url) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const renderErrorCodeGrid = () => {
    const filteredCodes = selectedCategory === 'all' 
      ? availableErrorCodes 
      : availableErrorCodes.filter(code => getCodeCategory(code) === selectedCategory);

    const batteryCodes = availableErrorCodes.filter(c => getCodeCategory(c) === 'battery').length;
    const motorCodes = availableErrorCodes.filter(c => getCodeCategory(c) === 'motor').length;
    const sensorCodes = availableErrorCodes.filter(c => getCodeCategory(c) === 'sensors').length;
    const communicationCodes = availableErrorCodes.filter(c => getCodeCategory(c) === 'communication').length;

    return (
      <div className="space-y-6">
        {/* Statistics */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div className="text-center p-3 bg-green-50 rounded border border-green-200">
            <div className="font-semibold text-green-800">🔋 {t('categoryStats.battery')}</div>
            <div className="text-green-600">{batteryCodes} {t('categoryStats.codes')}</div>
          </div>
          <div className="text-center p-3 bg-red-50 rounded border border-red-200">
            <div className="font-semibold text-red-800">⚙️ {t('categoryStats.motor')}</div>
            <div className="text-red-600">{motorCodes} {t('categoryStats.codes')}</div>
          </div>
          <div className="text-center p-3 bg-blue-50 rounded border border-blue-200">
            <div className="font-semibold text-blue-800">📡 {t('categoryStats.sensors')}</div>
            <div className="text-blue-600">{sensorCodes} {t('categoryStats.codes')}</div>
          </div>
          <div className="text-center p-3 bg-purple-50 rounded border border-purple-200">
            <div className="font-semibold text-purple-800">📱 {t('categoryStats.communication')}</div>
            <div className="text-purple-600">{communicationCodes} {t('categoryStats.codes')}</div>
          </div>
        </div>

        {/* Filter */}
        <div className="flex items-center space-x-4">
          <label className="text-sm font-medium text-gray-700">{t('errorList.filterLabel')}</label>
          <select 
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="border border-gray-300 rounded px-3 py-1 text-sm"
          >
            <option value="all">{t('errorList.allCategories')}</option>
            <option value="battery">{t('categories.BATTERY')}</option>
            <option value="motor">{t('categories.MOTOR')}</option>
            <option value="sensors">{t('categories.SENSORS')}</option>
            <option value="communication">{t('categories.COMMUNICATION')}</option>
            <option value="abs">{t('categories.ABS')}</option>
            <option value="brakes">{t('categories.BRAKES')}</option>
            <option value="iot">{t('categories.IOT')}</option>
            <option value="security">{t('categories.SECURITY')}</option>
          </select>
        </div>

        <p className="text-sm text-gray-600">
          {selectedCategory === 'all' ? (
            <>{t('errorList.totalCodes')} {availableErrorCodes.length} | 
            <span className="ml-2 text-red-600">E-XX: {availableErrorCodes.filter(c => c.startsWith('E-')).length}</span> | 
            <span className="ml-2 text-yellow-600">W-XX: {availableErrorCodes.filter(c => c.startsWith('W-')).length}</span> | 
            <span className="ml-2 text-blue-600">N-XX: {availableErrorCodes.filter(c => c.startsWith('N-')).length}</span></>
          ) : (
            <>{t('errorList.filteredCodes')} {filteredCodes.length} {t('errorList.onTotal')} {availableErrorCodes.length} | {t('errorList.category')} {t(`categories.${selectedCategory.toUpperCase()}`)}</>
          )}
        </p>
        
        <div className="grid grid-cols-4 md:grid-cols-6 lg:grid-cols-8 xl:grid-cols-10 gap-2">
          {filteredCodes.map((code) => {
            const category = getCodeCategory(code);
            const severityBorder = code.startsWith('E-') ? 'border-l-4 border-l-red-500' :
                                  code.startsWith('W-') ? 'border-l-4 border-l-yellow-500' :
                                  'border-l-4 border-l-blue-500';
            
            return (
              <button
                key={code}
                onClick={() => {
                  setErrorCode(code);
                  setSearchResult({
                    code,
                    category
                  });
                }}
                className={`px-3 py-2 rounded text-xs font-medium transition-colors ${getCategoryColor(category)} ${severityBorder}`}
                title={`${code} - ${t(`errorCodes.${code}.title`)}`}
              >
                {code}
              </button>
            );
          })}
        </div>

        {filteredCodes.length === 0 && selectedCategory !== 'all' && (
          <div className="text-center py-8 text-gray-500">
            <p>{t('errorList.noCodesFound')}</p>
          </div>
        )}
      </div>
    );
  };

  // Language Selector Component
  const LanguageSelector = () => (
    <div className="relative">
      <button
        onClick={() => setShowLanguageMenu(!showLanguageMenu)}
        className="flex items-center space-x-2 px-3 py-2 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
      >
        <Globe className="w-4 h-4" />
        <span className="text-lg">{currentLanguage.flag}</span>
        <span className="text-sm font-medium">{currentLanguage.name}</span>
        <ChevronDown className="w-4 h-4" />
      </button>
      
      {showLanguageMenu && (
        <div className="absolute right-0 mt-2 w-48 bg-white border border-gray-300 rounded-md shadow-lg z-50 max-h-60 overflow-y-auto">
          {languages.map((language) => (
            <button
              key={language.code}
              onClick={() => changeLanguage(language.code)}
              className={`w-full text-left px-4 py-2 hover:bg-gray-100 flex items-center space-x-3 ${
                i18n.language === language.code ? 'bg-blue-50 text-blue-700' : ''
              }`}
            >
              <span className="text-lg">{language.flag}</span>
              <span className="text-sm">{language.name}</span>
              {i18n.language === language.code && (
                <span className="ml-auto text-blue-600">✓</span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <img 
                src="/bafang-logo.png" 
                alt="Bafang Logo" 
                className="h-8 w-auto" 
              />
              <div>
                <h1 className="text-2xl font-bold text-gray-900">{t('header.title')}</h1>
                <p className="text-gray-600">{t('header.subtitle')}</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <InstallAppButton className="hidden sm:flex" />
              <LanguageSelector />
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="bg-white border-b">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex space-x-0">
            <button
              onClick={() => setActiveTab('decoder')}
              className={`px-6 py-3 font-medium border-b-2 transition-colors ${
                activeTab === 'decoder' 
                  ? 'border-orange-500 text-orange-600 bg-gray-50' 
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              {t('navigation.decoder')}
            </button>
            <button
              onClick={() => setActiveTab('common')}
              className={`px-6 py-3 font-medium border-b-2 transition-colors ${
                activeTab === 'common' 
                  ? 'border-orange-500 text-orange-600 bg-gray-50' 
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              {t('navigation.common')}
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8">
        {activeTab === 'decoder' && (
          <div className="space-y-6">
            {/* Decoder Section */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-xl font-semibold mb-2">{t('decoder.title')}</h2>
              <p className="text-gray-600 mb-4">{t('decoder.description')}</p>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {t('decoder.codeLabel')}
                  </label>
                  <input
                    type="text"
                    value={errorCode}
                    onChange={(e) => setErrorCode(e.target.value)}
                    placeholder={t('decoder.placeholder')}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                    onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                  />
                  <p className="text-sm text-gray-500 mt-1">
                    {t('decoder.helpText')}
                  </p>
                </div>
                
                <button
                  onClick={handleSearch}
                  className="bg-orange-500 hover:bg-orange-600 text-white px-6 py-2 rounded-md font-medium transition-colors flex items-center space-x-2"
                >
                  <Search className="w-4 h-4" />
                  <span>{t('decoder.buttonSearch')}</span>
                </button>
              </div>

              {/* Search Result */}
              {searchResult && (
                <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                  {searchResult.notFound ? (
                    <div>
                      <h3 className="font-semibold text-blue-900 mb-2">{t('decoder.notFound')}</h3>
                      <p className="text-blue-800">{t('decoder.notFoundDescription')}</p>
                    </div>
                  ) : (
                    <div>
                      <h3 className="font-semibold text-blue-900 mb-2">{t(`errorCodes.${searchResult.code}.title`)}</h3>
                      <p className="text-blue-800 mb-3">{t(`errorCodes.${searchResult.code}.description`)}</p>
                      
                      {/* Possibili cause */}
                      <div className="mb-4">
                        <h4 className="font-medium text-blue-900 mb-1">{t('decoder.causes')}</h4>
                        <ul className="list-disc list-inside text-sm text-blue-800">
                          {t(`errorCodes.${searchResult.code}.causes`, { returnObjects: true }).map((cause, index) => (
                            <li key={index}>{cause}</li>
                          ))}
                        </ul>
                      </div>
                      
                      {/* Soluzioni Hub Motor */}
                      <div className="mb-4">
                        <h4 className="font-medium text-blue-900 mb-2 flex items-center">
                          <span className="bg-blue-600 text-white px-2 py-1 rounded text-xs mr-2">{t('decoder.hubMotorLabel')}</span>
                          {t('decoder.hubMotorSolutions')}
                        </h4>
                        <div className="bg-blue-100 p-3 rounded border-l-4 border-blue-600">
                          <ul className="list-none text-sm text-blue-800 space-y-1">
                            {t(`errorCodes.${searchResult.code}.hubMotorSolutions`, { returnObjects: true }).map((solution, index) => (
                              <li key={index} className="flex items-start">
                                <span className="text-blue-600 mr-2">•</span>
                                <span>{solution}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>

                      {/* Soluzioni Central Motor */}
                      <div className="mb-4">
                        <h4 className="font-medium text-blue-900 mb-2 flex items-center">
                          <span className="bg-green-600 text-white px-2 py-1 rounded text-xs mr-2">{t('decoder.centralMotorLabel')}</span>
                          {t('decoder.centralMotorSolutions')}
                        </h4>
                        <div className="bg-green-100 p-3 rounded border-l-4 border-green-600">
                          <ul className="list-none text-sm text-green-800 space-y-1">
                            {t(`errorCodes.${searchResult.code}.centralMotorSolutions`, { returnObjects: true }).map((solution, index) => (
                              <li key={index} className="flex items-start">
                                <span className="text-green-600 mr-2">•</span>
                                <span>{solution}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Complete Error Code List */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <button
                onClick={() => toggleSection('errorCodes')}
                className="flex items-center justify-between w-full text-left"
              >
                <h2 className="text-xl font-semibold flex items-center space-x-2">
                  <Settings className="w-5 h-5 text-orange-500" />
                  <span>{t('errorList.title')}</span>
                </h2>
                {expandedSections.errorCodes ? (
                  <ChevronUp className="w-5 h-5 text-gray-500" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-gray-500" />
                )}
              </button>
              
              {expandedSections.errorCodes && (
                <div className="mt-4">
                  {renderErrorCodeGrid()}
                </div>
              )}
            </div>

            {/* Manual Search */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <button
                onClick={() => toggleSection('manuals')}
                className="flex items-center justify-between w-full text-left"
              >
                <h2 className="text-xl font-semibold flex items-center space-x-2">
                  <Book className="w-5 h-5 text-orange-500" />
                  <span>{t('manuals.title')}</span>
                </h2>
                {expandedSections.manuals ? (
                  <ChevronUp className="w-5 h-5 text-gray-500" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-gray-500" />
                )}
              </button>
              
              {/* Info Note */}
              <div className="mt-3 p-2 bg-blue-50 border border-blue-200 rounded text-xs text-blue-700">
                <strong>ℹ️ {t('manuals.infoNote')}</strong> {t('manuals.infoText')}
              </div>
              
              {expandedSections.manuals && (
                <div className="mt-4 space-y-6">
                  {/* Direct Bafang Links - 8 RIQUADRI UGUALI */}
                  <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                    <h3 className="font-medium mb-3 text-orange-800">{t('manuals.officialSite')}</h3>
                    <p className="text-sm text-gray-600 mb-4">
                      {t('manuals.officialDescription')}
                    </p>
                    
                    {/* RIGA 1 - 4 riquadri */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
                      <button
                        onClick={() => openManual(bafangLinks.userManual)}
                        className="flex flex-col items-center justify-center p-3 bg-white border border-orange-300 text-orange-700 rounded hover:bg-orange-100 transition-colors"
                      >
                        <Book className="w-5 h-5 mb-1" />
                        <span className="text-xs">{t('manuals.userManuals')}</span>
                      </button>
                      <button
                        onClick={() => openManual(bafangLinks.dealerManual)}
                        className="flex flex-col items-center justify-center p-3 bg-white border border-orange-300 text-orange-700 rounded hover:bg-orange-100 transition-colors"
                      >
                        <Settings className="w-5 h-5 mb-1" />
                        <span className="text-xs">{t('manuals.dealerManuals')}</span>
                      </button>
                      <button
                        onClick={() => openManual(bafangLinks.bafangGo)}
                        className="flex flex-col items-center justify-center p-3 bg-white border border-orange-300 text-orange-700 rounded hover:bg-orange-100 transition-colors"
                      >
                        <Smartphone className="w-5 h-5 mb-1" />
                        <span className="text-xs">{t('manuals.bafang_app')}</span>
                      </button>
                      <button
                        onClick={() => openManual(bafangLinks.besstPro)}
                        className="flex flex-col items-center justify-center p-3 bg-white border border-orange-300 text-orange-700 rounded hover:bg-orange-100 transition-colors"
                      >
                        <Settings className="w-5 h-5 mb-1" />
                        <span className="text-xs">{t('manuals.besst_pro')}</span>
                      </button>
                    </div>
                    
                    {/* RIGA 2 - 4 riquadri */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      <button
                        onClick={() => openManual(bafangLinks.catalogProducts)}
                        className="flex flex-col items-center justify-center p-3 bg-white border border-orange-300 text-orange-700 rounded hover:bg-orange-100 transition-colors"
                      >
                        <FileText className="w-5 h-5 mb-1" />
                        <span className="text-xs">{t('manuals.catalog')}</span>
                      </button>
                      <button
                        onClick={() => openManual(bafangLinks.products)}
                        className="flex flex-col items-center justify-center p-3 bg-white border border-orange-300 text-orange-700 rounded hover:bg-orange-100 transition-colors"
                      >
                        <Search className="w-5 h-5 mb-1" />
                        <span className="text-xs">{t('manuals.products')}</span>
                      </button>
                      <button
                        onClick={() => openManual(bafangLinks.support)}
                        className="flex flex-col items-center justify-center p-3 bg-white border border-orange-300 text-orange-700 rounded hover:bg-orange-100 transition-colors"
                      >
                        <Mail className="w-5 h-5 mb-1" />
                        <span className="text-xs">{t('manuals.contact')}</span>
                      </button>
                      <button
                        onClick={() => openManual(bafangLinks.faq)}
                        className="flex flex-col items-center justify-center p-3 bg-white border border-orange-300 text-orange-700 rounded hover:bg-orange-100 transition-colors"
                      >
                        <HelpCircle className="w-5 h-5 mb-1" />
                        <span className="text-xs">{t('manuals.faq')}</span>
                      </button>
                    </div>
                    
                    {/* Disclaimer */}
                    <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded text-sm text-yellow-800">
                      <strong>⚠️ {t('manuals.disclaimer')}</strong> {t('manuals.disclaimerText')}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'common' && (
          <div className="space-y-6">
            {/* Common Issues Section */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-xl font-semibold mb-4 flex items-center space-x-2">
                <AlertTriangle className="w-5 h-5 text-orange-500" />
                <span>{t('commonProblems.title')}</span>
              </h2>
              
              {/* Overview */}
              <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <p className="text-blue-800 text-sm mb-2">
                  <strong>🔋 {t('commonProblems.overview')}</strong> {t('commonProblems.overviewText')}
                </p>
                <div className="flex items-center space-x-4 text-sm text-blue-600">
                  <span>📊 {t('commonProblems.sections', { returnObjects: true }).length} {t('commonProblems.categoriesCount')}</span>
                  <span>🔧 {t('commonProblems.sections', { returnObjects: true }).reduce((total, section) => total + section.problems.length, 0)} {t('commonProblems.solutionsCount')}</span>
                </div>
              </div>
              
              {t('commonProblems.sections', { returnObjects: true }).map((section, sectionIndex) => (
                <div key={sectionIndex} className="mb-6">
                  <button
                    onClick={() => toggleSection(`common-${sectionIndex}`)}
                    className="flex items-center justify-between w-full text-left p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    <h3 className="font-medium text-gray-900 flex items-center">
                      <span className="text-xl mr-3">{section.icon}</span>
                      <span>{section.title}</span>
                      <span className="ml-2 text-sm text-gray-500 bg-gray-200 px-2 py-1 rounded-full">
                        {section.problems.length}
                      </span>
                    </h3>
                    {expandedSections[`common-${sectionIndex}`] ? (
                      <ChevronUp className="w-5 h-5 text-gray-500" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-gray-500" />
                    )}
                  </button>
                  
                  {expandedSections[`common-${sectionIndex}`] && (
                    <div className="mt-4 space-y-3">
                      {section.problems.map((problem, problemIndex) => (
                        <div key={problemIndex} className="bg-white p-4 rounded-lg border border-gray-200 hover:border-gray-300 transition-colors">
                          <h4 className="font-medium text-gray-900 mb-3 flex items-start">
                            <span className="text-orange-500 mr-2 mt-1">❓</span>
                            {problem.question}
                          </h4>
                          <div className="ml-7 p-3 bg-green-50 rounded border-l-4 border-green-500">
                            <p className="text-gray-700 text-sm leading-relaxed">
                              <span className="font-medium text-green-800 mr-2">💡 {t('commonProblems.solution')}</span>
                              {problem.answer}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="bg-white border-t mt-12">
        <div className="max-w-6xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <AlertTriangle className="w-5 h-5 text-orange-500" />
              <span className="text-sm font-medium">{t('footer.disclaimer')}</span>
            </div>
          </div>
          <p className="text-sm text-gray-600 mt-2">
            {t('footer.disclaimerText')}
          </p>
        </div>
      </div>
    </div>
  );
};

export default BafangDiagnosticApp